import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

/**
 * 使い方ガイドページ (Sprint BG-DataSettings 17、U-C33、L3)。
 *
 * 旧 `old/apps/user/src/pages/GuidePage.tsx` (228 行、Modal 版) を踏襲しつつ、v2 では
 * **page route として実装** (Drawer から `/guide` (or `/m/:urlToken/guide`) へ遷移)。
 *
 * 踏襲点 (旧 v1):
 *   - section の章立て (このアプリについて / 地図ビュー / 投稿する / 一覧ビュー / フィルター / そう思う！ / iPhone / Android / トラブルシューティング)
 *   - Section アコーディオン (defaultOpen=true / false 切替)
 *   - Dl / Steps / Tip / H4 ヘルパー
 *   - 文言の細部 (位置情報設定の段階説明、誤って拒否した場合の手順、PWA 再起動 hint)
 *
 * v2 差分:
 *   - Modal → 通常 page route。戻る link を上部に配置 (Drawer / ヘッダー導線統一)
 *   - Tailwind utility のみ (旧の brand-* → v2 primary-* / amber-* → warning-*)
 *   - 「リカバリーコードについて」「データ設定 / 分析 / アンケート / 検証」section 追加 (v2 で追加された機能)
 *   - react-markdown 等の dep は追加しない、JSX 直書きを維持 (旧と同 pattern)
 */

export function GuidePage() {
  const { urlToken } = useParams();
  const homeHref = urlToken ? `/m/${urlToken}` : '/';

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <Link
          to={homeHref}
          className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
        >
          ← ホーム
        </Link>
        <h1 className="text-base font-bold text-slate-900">使い方ガイド</h1>
        <div className="w-12" /> {/* layout balancer */}
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-3">
        <Section title="このアプリについて">
          <p>
            CitizenWalk
            は、まちを歩いて気づいたことを写真と位置情報で地図に記録し、参加者全員で共有するウェブアプリです。
          </p>
          <p className="mt-2">
            投稿された情報は地図ビューと一覧ビューで確認でき、「そう思う！」ボタンで共感を伝えることができます。
            分析タブからは AI
            解析結果を様々な角度で可視化でき、気づきカードや処方箋カードを作成できます。
          </p>
        </Section>

        <Section title="地図ビュー">
          <Dl label="地図の操作">
            ドラッグで移動、ピンチイン / アウトまたはボタンで拡大縮小できます。
          </Dl>
          <Dl label="投稿ピン">色付きの丸が投稿です。タップすると詳細を表示します。</Dl>
          <Dl label="未送信ピン">
            点線リング付きのオレンジのピンは、まだ送信されていない投稿です。電波が戻ると自動送信され、通常のピンに変わります（タップで「⏳
            未送信」と表示）。
          </Dl>
          <Dl label="クラスタ表示">
            投稿が密集している場合、数字付きのグレーの丸にまとめて表示されます。タップするとズームインします。
          </Dl>
          <Dl label="背景地図の切替">
            左上のボタンで標準・淡色・写真・OSM
            を切り替えられます。オフラインマップを保存済みの地区では「🗺 オフライン」も選べます。
          </Dl>
          <Dl label="凡例">左下の「凡例」をタップすると、色の意味を確認できます。</Dl>
          <Dl label="現在位置">青い点があなたの現在位置です（位置情報の許可が必要です）。</Dl>
          <Dl label="オフラインマップ">
            「📥
            オフラインマップ」から地区専用の地図をダウンロードしておくと、電波が弱い場所でも地図が表示されます。
          </Dl>
        </Section>

        <Section title="手書きメモ">
          <p>
            地図の上に直接手書きで書き込み、参加者と共有できます。気づいた場所に印をつけたり、ルートを描いたりできます。
          </p>
          <Dl label="開きかた">
            地図の右上にある「✎」ボタンをタップすると手書きモードになります。
          </Dl>
          <Dl label="ペン・消しゴム">
            色（7色）と太さ（3段階）を選んで線を描きます。「消しゴム」でなぞると消せます。
          </Dl>
          <Dl label="移動・選択">
            「✋ 移動」で手書きモードのまま地図を動かせます。「👆
            選択」で手書きをタップすると、描いた人と日時が表示されます。
          </Dl>
          <Dl label="取り消し・やり直し">「↩︎」「↪︎」で直前の操作を戻せます。</Dl>
          <Dl label="表示の切替">
            「自分 / みんな / セッション / 非表示」で、地図に重ねる手書きを切り替えられます。
          </Dl>
          <Dl label="完了">「完了」を押すと手書きモードを抜け、通常の地図操作に戻ります。</Dl>
          <Tip>
            あなたの手書きは自動で保存され、同じ地区の参加者にも共有されます（他の人の手書きは閲覧のみ）。「非表示」にして「完了」すると地図から消えます（消去ではなく非表示です）。手書きメモは、地区管理者が有効にした地区でのみ「✎」ボタンが表示されます。
          </Tip>
        </Section>

        <Section title="GIS レイヤー（地図に資料を重ねる）">
          <p>
            地区によっては、ハザードマップや区域図などの GIS
            データが地図に重ねて表示されます。地物は属性の値ごとに色分けされます。
          </p>
          <Dl label="地物の詳細">
            地図上の点・線・面をタップすると、属性情報がパネルで表示されます（パソコンでは右側、スマートフォンでは下から）。
          </Dl>
          <Dl label="GIS 設定">
            メニュー「🗺 GIS設定」から、レイヤーの表示
            ON/OFF・重なり順・見た目（色・不透明度・太さ・ラベル）を調整できます。
          </Dl>
          <Tip>
            GIS レイヤーと「🗺 GIS設定」は、管理者が GIS
            を有効にした地区でのみ表示されます。設定の変更はこの端末（ブラウザ）にのみ保存され、他の参加者には影響しません。
          </Tip>
        </Section>

        <Section title="投稿のしかた">
          <p>下部の「投稿」タブをタップして投稿画面を開きます。</p>
          <Dl label="写真">カメラで撮影するか、端末の写真を選択します。</Dl>
          <Dl label="位置情報">
            GPS
            で自動取得されます。別の場所を指定したいときは、地図のその地点を長押し（パソコンは右クリック）して「📍
            ここで投稿」を押すと、その位置で投稿できます。
          </Dl>
          <Dl label="タイトル">投稿のタイトルを入力します（必須）。</Dl>
          <Dl label="カテゴリ">
            地区の設定に応じて、防災 / ECW / ウォーカブル等のカテゴリやスコアを選択できます。
          </Dl>
          <Dl label="送信">すべての必須項目を入力したら「投稿する」ボタンで送信します。</Dl>
          <Dl label="オフライン時">
            電波がない場所でも投稿できます。投稿は端末に保存され（地図には点線リング付きのオレンジのピンで表示）、次にオンラインになったとき自動送信されます。送信待ちの間は画面上部に「⏳
            未送信 ◯」と表示されます。
          </Dl>
          <Tip>
            初めて投稿タブを開くと「撮影前の注意」が表示されます。内容を確認して「✓ 了解しました -
            続ける」を押すと写真を添付できるようになります。
          </Tip>
        </Section>

        <Section title="一覧ビュー">
          <p>下部の「一覧」タブをタップすると投稿の一覧を表示します。</p>
          <Dl label="表示順">
            上部のドロップダウンで、新しい順・古い順・「そう思う！」が多い順などに並び替えできます。
          </Dl>
          <Dl label="グループ化">
            日付・セッション・カテゴリなどでセクション分けして表示できます。
          </Dl>
          <Dl label="リスト / カード">ボタンでリスト表示とカード表示を切り替えられます。</Dl>
          <Dl label="写真サイズ">カード表示時、スライダーで写真の大きさを調整できます。</Dl>
        </Section>

        <Section title="フィルター・検索">
          <p>右上の🔍ボタンをタップするとフィルターパネルが開きます。</p>
          <p className="mt-1">
            キーワード、カテゴリ、日付、タグ、投稿者属性などで投稿を絞り込めます。フィルターは地図ビューと一覧ビューの両方に適用されます。
          </p>
        </Section>

        <Section title="「そう思う！」ボタン">
          <p>投稿に共感したら👍ボタンをタップしてください。もう一度タップすると取り消せます。</p>
        </Section>

        <Section title="リカバリーコードについて">
          <p>
            CitizenWalk では Firebase
            匿名認証を使用するため、端末を変えたりアプリを削除すると投稿履歴と紐付かなくなります。
            これを防ぐため「リカバリーコード」を発行して別端末に引き継ぐことができます。
          </p>
          <H4>コードを確認する</H4>
          <Steps
            steps={[
              'メニューを開く（左上の ☰ ボタン）',
              '「⚙ ニックネームと表示設定」をタップ',
              '画面内のリカバリコードを安全な場所に保存（コピー・QR 表示・スクリーンショット）',
            ]}
          />
          <H4>別端末から復帰する</H4>
          <Steps
            steps={[
              '新しい端末で同じ地区の URL を開く',
              'メニューから「⌨ ユーザー復帰コードの入力」をタップ',
              '元の端末で控えたリカバリーコードを入力',
            ]}
          />
          <H4>ホーム画面に追加したアプリへ移す</H4>
          <Steps
            steps={[
              'メニュー「📲 ホーム画面のアプリに移す」で引き継ぎ番号を発行',
              'ホーム画面に追加したアプリのトップ画面「ブラウザから引き継ぎ」欄に番号を入力',
            ]}
          />
          <p className="text-sm text-slate-600 mt-1">
            参加済みの地区がまとめて引き継がれます。引き継ぎ番号は発行から約 10 分・1
            回限り有効です。
          </p>
          <Tip>
            QR コード付き URL からアクセスした場合は <code>/recovery/auto</code>{' '}
            経由で自動復帰を試みます。
            紛失したらコードは再発行不可なので、必ず安全に保管してください。
          </Tip>
        </Section>

        <Section title="データ設定（ニックネーム・属性）">
          <p>
            メニューの「⚙
            ニックネームと表示設定」からニックネームや地区別の参加者属性（年代・性別等）を編集できます。
            投稿時に毎回入力する必要はなく、変更があった時だけ更新します。ユーザー復帰コードの確認もこの画面から行えます。
          </p>
        </Section>

        <Section title="自分の写真をまとめてダウンロード">
          <p>
            メニュー「📸 自分の写真をまとめてダウンロード」から、この地区であなたが投稿した写真を 1
            つの ZIP ファイルにまとめて保存できます（写真と投稿の対応表 manifest.csv 付き）。
          </p>
          <Steps
            steps={[
              'メニュー「📸 自分の写真をまとめてダウンロード」を開く',
              '「ZIP を作成」を押す（写真が多いと時間がかかります。画面を閉じずにお待ちください）',
              '表示されたリンクから「⬇ ダウンロード」、または「📋 リンクをコピー」で共有',
            ]}
          />
          <Tip>
            ダウンロードリンクの有効期限は約 24 時間です。期限が切れたら「🔄
            作り直す」で再生成してください。
          </Tip>
        </Section>

        <Section title="分析タブ（ECW モード地区のみ）">
          <p>
            ECW モードの地区では、メニューの「📊 分析アプリへ」から AI
            解析結果のマトリクス・ネットワークグラフ・
            ヒートマップなどの可視化と、気づきカード（観察 → 診断）・処方箋カード（処方箋ドラフト →
            期待効果）の 4-step 対話作成が利用できます。
          </p>
          <Tip>分析タブは地区管理者が有効化した場合のみ表示されます。</Tip>
        </Section>

        <Section title="アンケート">
          <p>
            メニュー「📋 アンケート」から、運営者から配信されたアンケートに回答できます。
            未回答のアンケートがある場合は、ホーム画面にバッジが表示されます。
          </p>
        </Section>

        <Section title="AI 判定のチェック">
          <p>
            メニュー「🤖 AI 判定をチェック」から、AI
            が付与した分類が妥当かどうかを参加者としてチェックできます。
            運営者が割り当てた担当範囲のみ表示されます。
          </p>
        </Section>

        <Section title="位置情報の設定（iPhone）" defaultOpen={false}>
          <p className="text-sm text-slate-500 mb-3">
            iPhone では位置情報の許可が 3
            段階に分かれています。すべてが「許可」になっている必要があります。
          </p>

          <H4>1. 端末の位置情報サービスを ON にする</H4>
          <Steps
            steps={[
              '「設定」アプリを開く',
              '「プライバシーとセキュリティ」をタップ',
              '「位置情報サービス」をタップ',
              '最上部のスイッチが ON（緑）になっていることを確認',
            ]}
          />

          <H4>2. Safari の位置情報を許可する</H4>
          <p className="text-sm text-slate-600 mb-1">
            <strong>iOS 17 以降:</strong>
          </p>
          <Steps
            steps={[
              '「設定」>「アプリ」>「Safari」をタップ',
              '「位置情報」をタップ',
              '「この App の使用中」または「次回または共有時に確認」を選択',
            ]}
          />
          <p className="text-sm text-slate-600 mb-1 mt-2">
            <strong>iOS 16 以前:</strong>
          </p>
          <Steps
            steps={[
              '「設定」>「プライバシーとセキュリティ」>「位置情報サービス」をタップ',
              '「Safari Websites」をタップ',
              '「この App の使用中」または「次回確認」を選択',
            ]}
          />

          <H4>3. サイトの許可を確認する</H4>
          <p className="text-sm text-slate-600 mb-1">
            初回アクセス時に「位置情報の利用を求めています」と表示されたら「許可」をタップしてください。
          </p>
          <p className="text-sm text-slate-600 mt-1">誤って拒否した場合:</p>
          <Steps
            steps={[
              '「設定」>「アプリ」>「Safari」>「Web サイトの設定」>「位置情報」',
              '該当サイトをタップして「許可」に変更',
            ]}
          />

          <H4>ホーム画面に追加した場合</H4>
          <p className="text-sm text-slate-600">
            ホーム画面に追加したアプリは Safari とは別に権限を管理します。
          </p>
          <Steps
            steps={[
              '「設定」>「プライバシーとセキュリティ」>「位置情報サービス」',
              'アプリ名をタップ',
              '「この App の使用中」を選択',
            ]}
          />

          <Tip>
            約 7 日間アクセスしないと Safari
            の許可がリセットされることがあります。ホーム画面に追加しておくと回避できます。
          </Tip>
        </Section>

        <Section title="位置情報の設定（Android）" defaultOpen={false}>
          <p className="text-sm text-slate-500 mb-3">
            Android では位置情報の許可が 3
            段階に分かれています。すべてが「許可」になっている必要があります。
          </p>

          <H4>1. 端末の位置情報を ON にする</H4>
          <Steps
            steps={[
              '「設定」>「位置情報」を開く',
              '位置情報のスイッチを ON にする',
              '「Google 位置情報の精度」をタップし、ON にする（屋内精度が向上します）',
            ]}
          />

          <H4>2. Chrome の位置情報権限を確認する</H4>
          <Steps
            steps={[
              '「設定」>「アプリ」>「Chrome」をタップ',
              '「権限」>「位置情報」をタップ',
              '「アプリの使用中のみ許可」を選択',
            ]}
          />
          <Tip>
            Android 12 以降では「正確な位置情報を使用」のスイッチも ON にしてください。OFF だと約
            100m の概算位置になります。
          </Tip>

          <H4>3. サイトの許可を確認する</H4>
          <p className="text-sm text-slate-600 mb-1">
            初回アクセス時に許可ダイアログが表示されたら「許可」をタップしてください。
          </p>
          <p className="text-sm text-slate-600 mt-1">誤って拒否した場合:</p>
          <Steps
            steps={[
              '該当サイトを開いた状態でアドレスバーの鍵アイコンをタップ',
              '「権限」>「位置情報」を「許可」に変更',
            ]}
          />

          <H4>ホーム画面に追加した場合</H4>
          <p className="text-sm text-slate-600">
            ホーム画面に追加したアプリは Chrome とは別に権限を管理します。
          </p>
          <Steps
            steps={[
              '「設定」>「アプリ」> アプリ名をタップ',
              '「権限」>「位置情報」>「アプリの使用中のみ許可」を選択',
              'Android 12 以降:「正確な位置情報を使用」を ON にする',
            ]}
          />
        </Section>

        <Section title="トラブルシューティング" defaultOpen={false}>
          <H4>位置情報が取得できない</H4>
          <Steps
            steps={[
              '端末の位置情報が ON か確認',
              'ブラウザ（Safari/Chrome）の位置情報権限を確認',
              'サイトごとの許可を確認',
              'ホーム画面アプリの場合は独自の権限を確認',
              'すべて OK なら屋外に移動してみてください（GPS 受信環境の問題）',
            ]}
          />
          <p className="text-sm text-slate-600 mt-1">
            位置情報が取れない場合でも、地図上をタップして手動で位置を指定できます。
          </p>

          <H4>地図が表示されない</H4>
          <p className="text-sm text-slate-600">
            ページをリロード（再読み込み）してください。改善しない場合はインターネット接続を確認してください。
            オフラインマップをダウンロード済みなら電波がなくても地図は表示されます。
          </p>

          <H4>投稿が送信できない</H4>
          <p className="text-sm text-slate-600">
            電波がない場所では投稿は端末に保存されます。オンラインに戻ると自動的に送信されます。
          </p>

          <H4>LINE や X（Twitter）から開いた場合</H4>
          <p className="text-sm text-slate-600">
            LINE・X（Twitter）などのアプリ内ブラウザでは位置情報やカメラが正常に動作しないことがあります。
            URL をコピーして Safari（iPhone）または Chrome（Android）で直接開いてください。
          </p>

          <H4>リカバリーコードを紛失した</H4>
          <p className="text-sm text-slate-600">
            残念ながらコードは再発行できません。地区管理者に連絡し、必要に応じて新しい匿名アカウントとして再参加してください。
          </p>
        </Section>

        <footer className="text-center text-xs text-slate-400 py-6">CitizenWalk v2 — Y.P.C.</footer>
      </main>
    </div>
  );
}

/* ─────────── ヘルパーコンポーネント (旧 GuidePage 踏襲、Tailwind class のみ v2 化) ─────────── */

function Section({
  title,
  defaultOpen = true,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
      >
        <span className="font-medium text-sm text-slate-800">{title}</span>
        <span className="text-slate-400 text-lg" aria-hidden="true">
          {open ? '−' : '＋'}
        </span>
      </button>
      {open && <div className="px-4 py-3 text-sm text-slate-700 space-y-2">{children}</div>}
    </div>
  );
}

function Dl({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="font-medium text-slate-800 shrink-0">{label}:</dt>
      <dd className="text-slate-600">{children}</dd>
    </div>
  );
}

function H4({ children }: { children: ReactNode }) {
  return <h4 className="font-semibold text-slate-800 mt-3 mb-1">{children}</h4>;
}

function Steps({ steps }: { steps: string[] }) {
  return (
    <ol className="list-decimal list-inside text-sm text-slate-600 space-y-0.5 ml-1">
      {steps.map((s, i) => (
        <li key={i}>{s}</li>
      ))}
    </ol>
  );
}

function Tip({ children }: { children: ReactNode }) {
  return (
    <div className="bg-warning-50 border border-warning-200 rounded px-3 py-2 text-xs text-warning-800 mt-2">
      {children}
    </div>
  );
}
